import { apiClient } from './client'
import type { OriginalTrophy } from './trophies'

export type Relation = 'friend' | 'pending_sent' | 'pending_received' | 'none'
export type RankingPeriod = 'weekly' | 'monthly' | 'yearly' | 'total'
export type ShelfState = 'visible' | 'private' | 'hidden'
export type ProfileRelation = 'self' | 'friend' | 'fof' | 'none'

export type FriendUser = {
  id: number
  nickname: string
  handle: string
  avatar_url: string
  total_points: number
}

export type FriendListItem = {
  id: number
  nickname: string
  handle: string
  avatar_url: string
  power: number
}

export type SearchResult = FriendListItem & { relation: Relation }

export type FriendRequest = {
  id: number
  sender: FriendUser
  receiver: FriendUser
  status: 'pending' | 'accepted' | 'rejected' | 'canceled'
  created_at: string
}

export type RankingRow = { rank: number; value: number; user: FriendUser }

export type Paginated<T> = { results: T[]; count: number }

export type RankingResponse = {
  period: RankingPeriod
  results: RankingRow[]
  count: number
  me: RankingRow | null
}

export type FriendProfile = {
  user: FriendListItem
  // 運（今年分）。戦闘力には含めない独立ステータスとして別表示する。
  luck: number
  relation: ProfileRelation
  // 申請ボタン制御用（pending_sent/received を含む）。relation とは別軸。
  request_relation: Relation
  shelf: { state: ShelfState; trophies?: OriginalTrophy[] }
}

export const getFriends = (offset = 0, limit = 10) =>
  apiClient
    .get<Paginated<FriendListItem>>('/friends/', { params: { offset, limit } })
    .then((r) => r.data)

export const getRanking = (period: RankingPeriod, offset = 0, limit = 10) =>
  apiClient
    .get<RankingResponse>('/friends/ranking/', { params: { period, offset, limit } })
    .then((r) => r.data)

export const searchUsers = (q: string, offset = 0, limit = 10) =>
  apiClient
    .get<Paginated<SearchResult>>('/friends/search/', { params: { q, offset, limit } })
    .then((r) => r.data)

export const getReceivedRequests = () =>
  apiClient.get<Paginated<FriendRequest>>('/friends/requests/received/').then((r) => r.data)

export const getSentRequests = () =>
  apiClient.get<Paginated<FriendRequest>>('/friends/requests/sent/').then((r) => r.data)

export const cancelRequest = (id: number) =>
  apiClient.post<FriendRequest>(`/friends/requests/${id}/cancel/`).then((r) => r.data)

export const sendFriendRequest = (receiverId: number) =>
  apiClient
    .post<FriendRequest>('/friends/requests/', { receiver_id: receiverId })
    .then((r) => r.data)

export const acceptRequest = (id: number) =>
  apiClient.post<FriendRequest>(`/friends/requests/${id}/accept/`).then((r) => r.data)

export const rejectRequest = (id: number) =>
  apiClient.post<FriendRequest>(`/friends/requests/${id}/reject/`).then((r) => r.data)

export const getFriendProfile = (userId: number) =>
  apiClient.get<FriendProfile>(`/friends/${userId}/profile/`).then((r) => r.data)

// 招待リンク（1ユーザー1本・DB保存）。発行/再発行・無効化・取得・閲覧。
export type Invite = { token: string; expires_at: string }

export const getInvite = () =>
  apiClient.get<{ invite: Invite | null }>('/friends/invite/').then((r) => r.data.invite)

export const createInvite = () =>
  apiClient.post<{ invite: Invite }>('/friends/invite/').then((r) => r.data.invite)

export const deleteInvite = () => apiClient.delete('/friends/invite/')

export const getInviteProfile = (token: string) =>
  apiClient.get<FriendProfile>(`/friends/invite/${token}/`).then((r) => r.data)

export const getFriendOfFriends = (userId: number, offset = 0, limit = 10) =>
  apiClient
    .get<Paginated<SearchResult>>(`/friends/${userId}/friends/`, { params: { offset, limit } })
    .then((r) => r.data)

export const unfriend = (userId: number) =>
  apiClient.delete(`/friends/${userId}/`)
